import type { ReactNode } from 'react';

import {
  App,
  CrucibleClientProvider,
  FoundryShellHeader,
  HostBridgeProvider,
  type HostBridge,
  SwitchToLocalFoundryButtonChrome,
} from '@volley/crucible-ui';

import { useCookieBoot, type CookieBootPhase } from './auth-v2/cookie-spa-boot.js';
import { beginLogin } from './auth-v2/login.js';
import { beginLogout } from './auth-v2/logout.js';
import { useRuntimeVersion } from './auth-v2/version-probe.js';
import { defaultVersionPollDependencies, useVersionPoll } from './version-poll.js';

export const DESKTOP_SWITCH_LOCAL_PATH = '/desktop/switch-local';
// crucible#2326 — sentinel the desktop-shell SPA navigates to so Electron main
// opens the native ProtoHub focused on a game. MUST match the main-side
// `CLOUD_PROTOHUB_PREVIEW_PATH` (crucible-gui app-mode-config.ts).
export const DESKTOP_PROTOHUB_PREVIEW_PATH = '/desktop/protohub-preview';

export function isDesktopSwitchLocalPath(location: Pick<Location, 'pathname'>): boolean {
  return location.pathname === DESKTOP_SWITCH_LOCAL_PATH;
}

export function isLikelyDesktopShell(userAgent: string): boolean {
  return /\bFoundryDesktop\//.test(userAgent) || /\bElectron\//.test(userAgent);
}

export function shouldUseDesktopOwnedCloudShell(userAgent: string): boolean {
  // Only the Foundry desktop app stamps this marker; generic Electron webviews keep CloudAppShell.
  return /\bFoundryDesktop\//.test(userAgent);
}

export function beginSwitchToLocal(location: Pick<Location, 'assign'> = window.location): never {
  location.assign(DESKTOP_SWITCH_LOCAL_PATH);
  throw new Error('beginSwitchToLocal: unreachable — top-level redirect must unload');
}

/**
 * crucible#2326 — ask the Electron host to open the native ProtoHub focused on
 * `gameId`. Unlike {@link beginSwitchToLocal}, this does NOT unload the SPA:
 * Electron main `preventDefault()`s the navigation (the SPA stays loaded behind
 * the simulator), so we just assign and return. A no-op in a plain browser (no
 * host intercepts it) — callers gate on the presence of a host bridge.
 */
export function beginProtoHubPreview(
  gameId: string,
  location: Pick<Location, 'assign'> = window.location,
): void {
  location.assign(`${DESKTOP_PROTOHUB_PREVIEW_PATH}?gameId=${encodeURIComponent(gameId)}`);
}

/**
 * crucible#2326 — the host bridge injected ONLY in the cloud desktop shell.
 * Stable module-level reference so consumers' callback deps don't churn.
 */
const DESKTOP_HOST_BRIDGE: HostBridge = {
  requestProtoHubPreview: (gameId) => beginProtoHubPreview(gameId),
};

function swallowNavigationUnreachable(err: unknown, prefix: string): void {
  if (err instanceof Error && err.message.startsWith(prefix)) return;
  throw err;
}

export interface RootProps {
  location?: Pick<Location, 'pathname'>;
  renderCloudApp?: () => React.JSX.Element;
}

export function Root({
  location = window.location,
  renderCloudApp = () => <FoundrySpa />,
}: RootProps): React.JSX.Element {
  if (isDesktopSwitchLocalPath(location)) {
    return <DesktopSwitchLocalFallback />;
  }
  return renderCloudApp();
}

export function DesktopSwitchLocalFallback(): React.JSX.Element {
  return (
    <main className="flex h-screen w-screen items-center justify-center bg-crucible-bg px-6 text-crucible-fg">
      <section className="w-full max-w-md rounded-lg border border-white/10 bg-crucible-surface p-6 shadow-2xl">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/35">
          Local Foundry
        </p>
        <h1 className="mt-3 text-xl font-semibold">Open Local Foundry from the desktop app</h1>
        <p className="mt-3 text-sm leading-6 text-white/55">
          The desktop app switches between Cloud Foundry and Local Foundry. In a browser, continue
          using Cloud Foundry.
        </p>
        <button
          type="button"
          className="mt-5 rounded-lg bg-crucible-accent px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90"
          onClick={() => {
            window.location.assign('/');
          }}
        >
          Return to Cloud Foundry
        </button>
      </section>
    </main>
  );
}

export interface CloudAppShellProps {
  phase: CookieBootPhase;
  showDesktopSwitch: boolean;
  showSignOut: boolean;
  onSwitchLocal: () => void;
  onSignOut: () => void;
  children: ReactNode;
}

export function CloudAppShell({
  phase,
  showDesktopSwitch,
  showSignOut,
  onSwitchLocal,
  onSignOut,
  children,
}: CloudAppShellProps): React.JSX.Element {
  return (
    <div
      data-testid="cloud-app-shell"
      data-phase={phase}
      className="flex h-screen w-screen flex-col overflow-hidden bg-crucible-bg text-crucible-fg"
    >
      <FoundryShellHeader
        chrome="web"
        title="Cloud Foundry"
        testId="cloud-app-shell-header"
        actions={
          <>
            {showDesktopSwitch && <SwitchToLocalFoundryButtonChrome onClick={onSwitchLocal} />}
            {showSignOut && (
              <button
                type="button"
                onClick={onSignOut}
                className="rounded-lg border border-white/10 px-3 py-1.5 text-sm text-white/60 transition-colors hover:border-white/20 hover:text-white/85"
              >
                Sign out
              </button>
            )}
          </>
        }
      />
      <div className="min-h-0 flex-1 overflow-hidden">{children}</div>
    </div>
  );
}

export interface FoundrySpaProps {
  boot?: ReturnType<typeof useCookieBoot>;
  versionUpdateAvailable?: boolean;
  userAgent?: string;
}

export function FoundrySpa({
  boot,
  versionUpdateAvailable,
  userAgent = navigator.userAgent,
}: FoundrySpaProps = {}): React.JSX.Element {
  const liveBoot = useCookieBoot();
  const liveVersionPoll = useVersionPoll(defaultVersionPollDependencies());
  const effectiveBoot = boot ?? liveBoot;
  const effectiveVersionUpdateAvailable = versionUpdateAvailable ?? liveVersionPoll.updateAvailable;
  const isDesktopShell = shouldUseDesktopOwnedCloudShell(userAgent);

  const handleSwitchLocal = (): void => {
    try {
      beginSwitchToLocal();
    } catch (err) {
      swallowNavigationUnreachable(err, 'beginSwitchToLocal: unreachable');
    }
  };

  const handleSignOut = (): void => {
    try {
      beginLogout();
    } catch (err) {
      swallowNavigationUnreachable(err, 'beginLogout: unreachable');
    }
  };

  if (isDesktopShell) {
    return (
      <div
        data-testid="cloud-app-content"
        data-phase={effectiveBoot.phase}
        className="h-screen w-screen overflow-hidden bg-crucible-bg text-crucible-fg"
      >
        <CookieBootContent
          boot={effectiveBoot}
          versionUpdateAvailable={effectiveVersionUpdateAvailable}
          hostBridge={DESKTOP_HOST_BRIDGE}
        />
        <RuntimeVersionFooter />
      </div>
    );
  }

  return (
    <CloudAppShell
      phase={effectiveBoot.phase}
      showDesktopSwitch={false}
      showSignOut={effectiveBoot.phase === 'ready' || effectiveBoot.phase === 'connect-error'}
      onSwitchLocal={handleSwitchLocal}
      onSignOut={handleSignOut}
    >
      <CookieBootContent
        boot={effectiveBoot}
        versionUpdateAvailable={effectiveVersionUpdateAvailable}
      />
      <RuntimeVersionFooter />
    </CloudAppShell>
  );
}

function CookieBootContent(props: {
  boot: ReturnType<typeof useCookieBoot>;
  versionUpdateAvailable: boolean;
  /** crucible#2326 — present only in the desktop shell; null in pure-web. */
  hostBridge?: HostBridge | null;
}): React.JSX.Element {
  const { boot, versionUpdateAvailable, hostBridge = null } = props;
  if (boot.phase !== 'ready' || !boot.client) {
    return (
      <PreReadyShell
        phase={boot.phase}
        userLabel={boot.user?.userId}
        errorReason={boot.errorReason}
        versionUpdateAvailable={versionUpdateAvailable}
        beginLogin={beginLogin}
      />
    );
  }
  return (
    <>
      <VersionUpdateLiveRegion updateAvailable={versionUpdateAvailable} />
      <CrucibleClientProvider client={boot.client}>
        <HostBridgeProvider value={hostBridge}>
          <App frame="embedded" />
        </HostBridgeProvider>
      </CrucibleClientProvider>
    </>
  );
}

function RuntimeVersionFooter(): React.JSX.Element | null {
  const v = useRuntimeVersion();
  if (v.kind === 'loading') return null;
  const label =
    v.kind === 'ready'
      ? `Crucible v${v.info.runtimeVersion} (${v.info.mode})`
      : 'Crucible runtime unknown';
  return (
    <footer
      data-testid="version-info"
      data-version-kind={v.kind}
      style={{
        position: 'fixed',
        bottom: 4,
        right: 8,
        fontSize: 11,
        opacity: 0.5,
        fontFamily: 'monospace',
        pointerEvents: 'none',
        userSelect: 'none',
      }}
    >
      {label}
    </footer>
  );
}

function PreReadyShell(props: {
  phase: string;
  userLabel?: string;
  errorReason?: string;
  versionUpdateAvailable: boolean;
  beginLogin: () => void;
}): React.JSX.Element {
  const title =
    props.phase === 'connect-error'
      ? 'Could not open Cloud Foundry'
      : props.phase === 'probing'
        ? 'Opening Cloud Foundry'
        : 'Sign in to Cloud Foundry';

  return (
    <main className="flex h-full items-center justify-center px-6">
      <section className="w-full max-w-md rounded-lg border border-white/10 bg-crucible-surface p-6 shadow-2xl">
        <h1 data-testid="bg-0-placeholder" className="text-xl font-semibold">
          {title}
        </h1>

        <VersionUpdateLiveRegion updateAvailable={props.versionUpdateAvailable} />

        <div data-testid="boot-phase" className="mt-3 text-xs text-white/35">
          {props.phase}
        </div>

        {props.phase === 'signed-out' || props.phase === 'idle' ? (
          <button
            type="button"
            data-testid="sign-in-button"
            className="mt-5 rounded-lg bg-crucible-accent px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90"
            onClick={() => {
              try {
                props.beginLogin();
              } catch (err) {
                swallowNavigationUnreachable(err, 'beginLogin: unreachable');
              }
            }}
          >
            Sign in
          </button>
        ) : null}

        {props.phase === 'probing' ? (
          <p data-testid="auth-status" className="mt-4 text-sm text-white/55">
            Checking session...
          </p>
        ) : null}

        {props.phase === 'connect-error' ? (
          <p data-testid="auth-status" className="mt-4 text-sm leading-6 text-white/55">
            Session restore failed: {props.errorReason ?? 'unknown error'}
          </p>
        ) : null}

        {props.userLabel ? (
          <p className="mt-3 truncate text-xs text-white/35">{props.userLabel}</p>
        ) : null}
      </section>
    </main>
  );
}

function VersionUpdateLiveRegion(props: { updateAvailable: boolean }): React.JSX.Element {
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid={props.updateAvailable ? 'version-update-banner' : undefined}
      className={props.updateAvailable ? 'mt-4 text-sm text-white/70' : undefined}
    >
      {props.updateAvailable ? (
        <>
          <span>A new version of Foundry is available.</span>{' '}
          <button
            type="button"
            data-testid="version-update-reload"
            onClick={() => {
              window.location.reload();
            }}
            className="underline decoration-white/30 underline-offset-4 hover:decoration-white/70"
          >
            Reload to update
          </button>
        </>
      ) : null}
    </div>
  );
}
